import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CaseStudyItem } from '@/types/case-study';
import { RichTextDisplay } from '@/components/ui/rich-text-display';
import { filterEmptySections } from '@/utils/caseStudyUtils';
import { ContentBlockRenderer } from '@/components/case-study/content-blocks/ContentBlockRenderer';

interface PublicCaseStudyViewProps {
  caseStudy: CaseStudyItem;
}

export const PublicCaseStudyView: React.FC<PublicCaseStudyViewProps> = ({ caseStudy }) => {
  // Filter out empty sections for public view
  const sectionsWithContent = filterEmptySections(caseStudy.sections);
  const [zoomedImage, setZoomedImage] = useState<{ url: string; alt: string } | null>(null);
  const [isZoomActive, setIsZoomActive] = useState(false);
  const [isCarouselImage, setIsCarouselImage] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  const closeZoom = useCallback(() => {
    setIsZoomActive(false);

    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = window.setTimeout(() => {
      setZoomedImage(null);
      setIsCarouselImage(false);
      closeTimeoutRef.current = null;
    }, 250);
  }, []);

  const openZoom = useCallback((image: { url: string; alt: string }) => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setZoomedImage(image);

    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = window.requestAnimationFrame(() => {
      setIsZoomActive(true);
    });
  }, []);

  useEffect(() => {
    if (!zoomedImage) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeZoom();
      } else if (isCarouselImage && (window as any).__carouselNavigation) {
        const carouselNav = (window as any).__carouselNavigation;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          carouselNav.prevSlide();
          setTimeout(() => {
            const carouselImage = document.querySelector('[data-carousel-image="true"]') as HTMLImageElement;
            if (carouselImage) {
              setZoomedImage({
                url: carouselImage.src,
                alt: carouselImage.alt || 'Carousel image'
              });
            }
          }, 50);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          carouselNav.nextSlide();
          setTimeout(() => {
            const carouselImage = document.querySelector('[data-carousel-image="true"]') as HTMLImageElement;
            if (carouselImage) {
              setZoomedImage({
                url: carouselImage.src,
                alt: carouselImage.alt || 'Carousel image'
              });
            }
          }, 50);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [zoomedImage, closeZoom, isCarouselImage]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsMounted(true);
    return () => {
      setIsMounted(false);
    };
  }, []);

  const handleCarouselNav = useCallback((direction: 'next' | 'prev') => {
    const carouselNav = (window as any).__carouselNavigation;
    if (!carouselNav) return;

    if (direction === 'next') {
      carouselNav.nextSlide();
    } else {
      carouselNav.prevSlide();
    }

    // Update the zoomed image to the new carousel image
    setTimeout(() => {
      const carouselImage = document.querySelector('[data-carousel-image="true"]') as HTMLImageElement;
      if (carouselImage) {
        setZoomedImage({
          url: carouselImage.src,
          alt: carouselImage.alt || 'Carousel image'
        });
      }
    }, 50);
  }, []);

  const handleImageClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const imageElement = target.closest('img');

      if (!imageElement || !(imageElement instanceof HTMLImageElement)) {
        return;
      }

      if (imageElement.dataset.noZoom === 'true') {
        return;
      }

      if (!imageElement.src) {
        return;
      }

      event.preventDefault();

      // Check if this is a carousel image
      const isCarousel = imageElement.dataset.carouselImage === 'true';
      setIsCarouselImage(isCarousel);

      openZoom({
        url: imageElement.src,
        alt: imageElement.alt || 'Case study image'
      });
    },
    [openZoom]
  );

  return (
    <div
      className="max-w-5xl mx-auto space-y-8 [&_img]:cursor-zoom-in [&_img]:transition-transform [&_img]:duration-200 [&_img]:ease-out [&_img]:shadow-[0_0_12px_-3px_rgba(15,23,42,0.14)] [&_img:hover]:scale-[1.02]"
      onClick={handleImageClick}
    >
      {/* Header */}
      <div className="space-y-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
          {caseStudy.title}
        </h1>
        {caseStudy.description && (
          <div className="prose prose-sm max-w-none text-gray-600 md:text-justify leading-6">
            <RichTextDisplay 
              content={caseStudy.description} 
              className="[&_p]:text-sm [&_p]:text-gray-600 [&_p]:md:text-justify [&_p]:leading-6"
            />
          </div>
        )}
      </div>

      {/* Cover Image */}
      {caseStudy.cover_image && (
        <div className="w-full bg-gray-50 rounded-lg overflow-hidden p-2 md:p-4">
          <img
            src={caseStudy.cover_image}
            alt={caseStudy.title}
            className="w-full h-auto rounded-xl object-contain shadow-[0_0_14px_-3px_rgba(15,23,42,0.16)]"
            onClick={() => openZoom({ url: caseStudy.cover_image, alt: caseStudy.title })}
          />
        </div>
      )}

      {/* Sections - Only show sections with content */}
      {sectionsWithContent.length > 0 ? (
        <div className="space-y-12">
          {sectionsWithContent.map((section) => (
            <div key={section.id} className="space-y-6">
              <h2 className="text-2xl font-semibold text-gray-900 border-b border-gray-200 pb-2">
                {section.title}
              </h2>
              
              {/* Rich Text Content */}
              {section.content && (
                <div className="prose prose-lg max-w-none text-gray-700">
                        <RichTextDisplay 
                          content={section.content} 
                          className="[&_ul]:space-y-2 [&_ol]:space-y-2 [&_li]:leading-relaxed [&_li_p]:my-0 [&_li_p]:mt-0 [&_li_p]:mb-0 [&_p]:text-sm"
                        />
                </div>
              )}

              {/* Images */}
              {section.images && section.images.length > 0 && (
                <div className="space-y-6">
                  {section.images.map((image) => (
                    <div key={image.id} className="space-y-3">
                      <div className="w-full bg-gray-50 rounded-lg overflow-hidden p-9">
                        <img
                          src={image.url} 
                          alt={image.caption || "Case study image"}
                          className="w-full h-auto object-contain shadow-[0_0_14px_-3px_rgba(15,23,42,0.16)] rounded-xl"
                          onClick={() => openZoom({ url: image.url, alt: image.caption || "Case study image" })}
                        />
                      </div>
                      {image.caption && (
                        <p className="text-sm text-gray-600 text-center italic">
                          {image.caption}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Content Blocks */}
              {section.content_blocks && section.content_blocks.length > 0 && (
                <div className="space-y-6">
                  {section.content_blocks
                    .sort((a, b) => a.order - b.order)
                    .map((block) => (
                      <ContentBlockRenderer
                        key={block.id}
                        block={block}
                        onUpdate={() => {}} // No updates in public view
                        onRemove={() => {}} // No removal in public view
                        isActive={false} // Not editable in public view
                      />
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">
            This case study doesn't have any content sections yet.
          </p>
        </div>
      )}

      {zoomedImage && isMounted &&
        createPortal(
          <div
            className={`fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isZoomActive ? 'opacity-100' : 'opacity-0'}`}
            role="dialog"
            aria-modal="true"
            aria-label="Zoomed case study image"
            onClick={closeZoom}
          >
            <img
              src={zoomedImage.url}
              alt={zoomedImage.alt}
              data-no-zoom="true"
              className={`max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl transition-transform duration-300 ease-out ${isZoomActive ? 'scale-100' : 'scale-95'} cursor-zoom-out`}
              onClick={(event) => event.stopPropagation()}
            />

            {/* Carousel Navigation - only show for carousel images */}
            {isCarouselImage && (window as any).__carouselNavigation && (
              <>
                {/* Previous button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCarouselNav('prev');
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors backdrop-blur-sm"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                {/* Next button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCarouselNav('next');
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 text-white rounded-full flex items-center justify-center hover:bg-white/20 transition-colors backdrop-blur-sm"
                  aria-label="Next image"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                {/* Slide counter */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1.5 rounded-full">
                  {(window as any).__carouselNavigation.currentSlide + 1} / {(window as any).__carouselNavigation.totalSlides}
                </div>
              </>
            )}
          </div>,
          document.body
        )
      }
    </div>
  );
};

export default PublicCaseStudyView;
